import { lazy, Suspense } from "react";
import { WEB_URL } from "@/lib/webUrl";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import i18n from "@/i18n";
import type { Activity, AdminInspirationListItem } from "@ckgo2/shared/types";
import type { UseFormReturn } from "react-hook-form";
import {
  ArrowLeft,
  Save,
  Loader2,
  FileText,
  Image,
  List,
  Type,
  Video,
  Lightbulb,
  Link2,
} from "lucide-react";
import SaveSuccessIndicator from "@/components/common/SaveSuccessIndicator";
import FieldHint from "@/components/common/FieldHint";
import TabWebLink from "@/components/common/TabWebLink";
import { MediaPickerScopeProvider } from "@/components/common/MediaPickerScope";
import { inspirationHintRegistry } from "@/lib/webHints/inspiration";
import { tabWebTarget } from "@/lib/webHints/variants";
import { useEditPage } from "@/hooks/useEditPage";
import { useAdminInspirationList } from "@/hooks/useAdminLists";
import { useEditorTab, useSwitchTabOnSaveError } from "@/hooks/useEditorTab";
import { ACTIVITY_FIELD_TO_TAB } from "@/lib/fieldTabMap";
import ErrorBanner from "@/components/common/ErrorBanner";
import type { ActivityFormData } from "@/components/activities/types";
import {
  ACTIVITY_FORM_DEFAULTS,
  activityToForm,
  inspirationPageUrl,
  inspirationPublished,
} from "@/components/activities/activityEditor";

import DescriptionTab from "@/components/activities/DescriptionTab";
import GalleryTab from "@/components/activities/GalleryTab";

const BasicInfoTab = lazy(() => import("@/components/activities/BasicInfoTab"));
const IncludedTagsTab = lazy(() => import("@/components/activities/IncludedTagsTab"));
const VideosTab = lazy(() => import("@/components/activities/VideosTab"));
const TipsTab = lazy(() => import("@/components/activities/TipsTab"));
const RelatedTab = lazy(() => import("@/components/activities/RelatedTab"));

const TABS = [
  { id: "basic", label: i18n.t("content.activities.tabs.basic"), icon: FileText },
  { id: "description", label: i18n.t("content.activities.tabs.description"), icon: Type },
  { id: "gallery", label: i18n.t("content.activities.tabs.gallery"), icon: Image },
  { id: "included", label: i18n.t("content.activities.tabs.included"), icon: List },
  { id: "videos", label: i18n.t("content.activities.tabs.videos"), icon: Video },
  { id: "tips", label: i18n.t("content.activities.tabs.tips"), icon: Lightbulb },
  { id: "related", label: i18n.t("content.activities.tabs.related"), icon: Link2 },
];
const TAB_IDS = TABS.map((tab) => tab.id);

function ActivityTabContent({
  activeTab,
  form,
  allActivities,
  currentId,
}: {
  activeTab: string;
  form: UseFormReturn<ActivityFormData>;
  allActivities: AdminInspirationListItem[];
  currentId?: string;
}) {
  switch (activeTab) {
    case "basic":
      return <BasicInfoTab form={form} />;
    case "description":
      return <DescriptionTab form={form} />;
    case "gallery":
      return <GalleryTab form={form} />;
    case "included":
      return <IncludedTagsTab form={form} />;
    case "videos":
      return <VideosTab form={form} />;
    case "tips":
      return <TipsTab form={form} />;
    case "related":
      return (
        <RelatedTab
          form={form}
          allActivities={allActivities.filter((a) => a.id !== currentId)}
          currentId={currentId}
        />
      );
    default:
      return null;
  }
}

export default function ActivityEditPage() {
  const { t } = useTranslation();
  const [activeTab, setTab] = useEditorTab(TAB_IDS);

  // Štíhlý sdílený seznam inspirací pro kartu Související. Klíč začíná `listKey`
  // "activities", takže ho uložení inspirace obnoví. Destinace si bere výběr destinace sám
  // (`DestinationPicker` → `useAdminDestinationList`, sdílená keš seznamu).
  const { data: allActivitiesData } = useAdminInspirationList();

  const { isNew, isLoading, form, saveMutation, onSubmit, itemData, queryClient } = useEditPage<
    ActivityFormData,
    Activity
  >({
    resourceKey: "activity",
    listKey: "activities",
    apiPath: "/api/admin/inspirations",
    defaultValues: ACTIVITY_FORM_DEFAULTS,
    navigateTo: "/inspirations",
    slugSourceField: "title",
    // Text kategorie starých plochých tipů dodává slovník; převod sám je čistý (`activityEditor`).
    transformOnLoad: (a) => activityToForm(a, i18n.t("shared.faqEditor.defaultTopic")),
    // Bez `resetDeps`: seznam destinací do resetu formuláře nepatří (jeho dojetí po
    // invalidaci uložením by formulář resetovalo na možná ještě neobnovený detail).
    // Výběr destinace je řízený `Controller`em, takže hodnotu ukáže i nad pozdě dojetým seznamem.
  });

  useSwitchTabOnSaveError(saveMutation.error, ACTIVITY_FIELD_TO_TAB, setTab);

  // `useEditPage` invaliduje sám `["activities"]` a vlastní detail. Změna destinace inspirace
  // ale mění i detaily destinací (`["destination", id]` nese `inspirationIds`): detail ze
  // staré keše by inspiraci při dalším uložení destinace přes `syncDestinationInspirations`
  // tiše vrátil k původní destinaci. Proto po úspěchu (při chybě `onSubmit` vyhodí dál)
  // prefix `["destination"]`; neaktivní dotaz se jen označí za zastaralý.
  const onSubmitAndInvalidate = async (data: ActivityFormData) => {
    await onSubmit(data);
    await queryClient.invalidateQueries({ queryKey: ["destination"] });
  };

  const allActivities = allActivitiesData?.data || [];

  const {
    register,
    handleSubmit,
    formState: { isSubmitting },
  } = form;

  // Odkaz „Zobrazit na webu“ z ULOŽENÉHO detailu: kanonická adresa (`countrySlug` z admin
  // detailu) a stav publikace podle uloženého `status`, ne podle rozpracovaného formuláře.
  const pageUrl = inspirationPageUrl(itemData, WEB_URL);
  const published = inspirationPublished(itemData, isNew);

  if (isLoading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <div className="border-primary-500 h-8 w-8 animate-spin rounded-full border-4 border-t-transparent" />
      </div>
    );
  }

  if (!isNew && !isLoading && !itemData) {
    return (
      <div className="flex h-64 flex-col items-center justify-center gap-4">
        <p className="text-lg text-gray-600 dark:text-gray-400">
          {t("content.activities.edit.notFound")}
        </p>
        <Link to="/inspirations" className="btn-primary">
          <ArrowLeft className="h-5 w-5" />
          {t("content.activities.edit.backToList")}
        </Link>
      </div>
    );
  }

  // Země okna výběru fotek (F7.11) podle destinace vybrané ve formuláři, i před uložením.
  const pickerDestinationId = form.watch("destinationId") || null;

  const page = (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Link
            to="/inspirations"
            className="rounded-lg p-2 hover:bg-gray-100 dark:hover:bg-gray-700"
          >
            <ArrowLeft className="h-5 w-5 text-gray-600 dark:text-gray-300" />
          </Link>
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
              {isNew
                ? t("content.activities.edit.newTitle")
                : t("content.activities.edit.editTitle")}
            </h1>
            <p className="mt-1 text-gray-600 dark:text-gray-400">
              {isNew
                ? t("content.activities.edit.newSubtitle")
                : t("content.activities.edit.editSubtitle", { title: itemData?.title })}
            </p>
          </div>
        </div>

        <div className="flex items-center gap-3">
          <SaveSuccessIndicator isSuccess={saveMutation.isSuccess} />
          {/* Odkaz hlavičky: stránka bez kotvy a bez obalu `data-tab-web-link`. */}
          <TabWebLink
            target={{ anchor: null }}
            pageUrl={pageUrl}
            published={published}
            isNew={isNew}
          />
          {!isNew && (
            <div className="flex w-40 flex-col">
              <select {...register("status")} className="input w-40">
                <option value="draft">{t("common.status.draft")}</option>
                <option value="published">{t("common.status.published")}</option>
                <option value="archived">{t("common.status.archived")}</option>
              </select>
              <FieldHint
                registry={inspirationHintRegistry}
                field="status"
                variant="inspiration"
                className="w-40"
              />
            </div>
          )}
          <button
            type="submit"
            form="activity-form"
            disabled={isSubmitting}
            className="btn-primary"
          >
            {isSubmitting ? (
              <>
                <Loader2 className="h-5 w-5 animate-spin" />
                {t("common.actions.saving")}
              </>
            ) : (
              <>
                <Save className="h-5 w-5" />
                {t("common.actions.save")}
              </>
            )}
          </button>
        </div>
      </div>

      <ErrorBanner error={saveMutation.error} />

      {/* Tabs */}
      <div className="border-b border-gray-200 dark:border-gray-700">
        <nav className="-mb-px flex gap-4 overflow-x-auto">
          {TABS.map((tab) => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                type="button"
                onClick={() => setTab(tab.id)}
                className={`flex items-center gap-2 whitespace-nowrap border-b-2 px-1 py-3 text-sm font-medium transition-colors ${
                  activeTab === tab.id
                    ? "border-primary-500 text-primary-600"
                    : "border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700"
                }`}
              >
                <Icon className="h-4 w-4" />
                {tab.label}
              </button>
            );
          })}
        </nav>
      </div>

      {/* Odkaz záložky nad jejím obsahem */}
      <div data-tab-web-link={activeTab}>
        <TabWebLink
          target={tabWebTarget(inspirationHintRegistry, activeTab, "inspiration")}
          pageUrl={pageUrl}
          published={published}
          isNew={isNew}
        />
      </div>

      {/* Form */}
      <form id="activity-form" onSubmit={handleSubmit(onSubmitAndInvalidate)}>
        <Suspense
          fallback={
            <div className="flex h-32 items-center justify-center">
              <Loader2 className="text-primary-600 h-6 w-6 animate-spin" />
            </div>
          }
        >
          <ActivityTabContent
            activeTab={activeTab}
            form={form}
            allActivities={allActivities}
            currentId={itemData?.id}
          />
        </Suspense>
      </form>
    </div>
  );

  return (
    <MediaPickerScopeProvider destinationId={pickerDestinationId}>{page}</MediaPickerScopeProvider>
  );
}
